/**
 * TeacherLogin Component
 * 
 * Login form for teachers to access the teacher panel.
 * Uses Moodle credentials for authentication.
 * Stores JWT token in localStorage upon successful login.
 * 
 * Requirements: 7.1, 7.4
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Lock, Loader2, LogIn, AlertCircle, GraduationCap } from 'lucide-react';

/**
 * Payment Service base URL from environment variable
 * Defaults to localhost:3001 for development
 */
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';

/**
 * LocalStorage key for teacher token
 */
const TEACHER_TOKEN_KEY = 'teacher_token';
const TEACHER_DATA_KEY = 'teacher_data';

/**
 * Authenticates teacher with Moodle credentials
 * @param {string} username - Moodle username
 * @param {string} password - Moodle password
 * @returns {Promise<Object>} - Authentication response
 */
async function authenticateTeacher(username, password) {
  const response = await fetch(`${PAYMENT_API_URL}/api/teacher/auth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Error al iniciar sesión');
  }

  return data;
}

/**
 * Stores teacher authentication data in localStorage
 * @param {string} token - JWT token
 * @param {Object} teacher - Teacher data
 * @param {string} expiresAt - Token expiration timestamp
 */
function storeAuthData(token, teacher, expiresAt) {
  localStorage.setItem(TEACHER_TOKEN_KEY, token);
  localStorage.setItem(TEACHER_DATA_KEY, JSON.stringify({
    ...teacher,
    expiresAt
  }));
}

/**
 * TeacherLogin Component
 * 
 * @param {Object} props
 * @param {Function} props.onLoginSuccess - Callback when login is successful
 */
export default function TeacherLogin({ onLoginSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous error
    setError(null);

    // Validate inputs
    if (!username.trim()) {
      setError('Ingresa tu nombre de usuario');
      return;
    }

    if (!password) {
      setError('Ingresa tu contraseña');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authenticateTeacher(username.trim(), password);

      if (response.success) {
        // Store token and teacher data in localStorage
        storeAuthData(response.token, response.teacher, response.expiresAt);

        // Call success callback
        if (onLoginSuccess) {
          onLoginSuccess(response.teacher, response.token);
        }
      } else {
        throw new Error(response.error || 'Error al iniciar sesión');
      }
    } catch (err) {
      // Requirements 7.4: Show error message on failed login
      setError(err.message || 'Usuario o contraseña incorrectos');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-md mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
          <GraduationCap className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Panel de Profesor
        </h2>
        <p className="text-slate-400">
          Ingresa con tus credenciales de Moodle
        </p>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Username Field */}
        <div>
          <label 
            htmlFor="username" 
            className="block text-sm font-medium text-slate-300 mb-2"
          >
            Usuario
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <User className="w-5 h-5 text-slate-500" aria-hidden="true" />
            </div>
            <input
              type="text"
              id="username"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Tu usuario de Moodle"
              autoComplete="username"
              disabled={isLoading}
              className={`
                w-full pl-12 pr-4 py-3 bg-white/5 border rounded-xl
                text-white placeholder-slate-500
                focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                focus:ring-offset-2 focus:ring-offset-slate-900
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                border-white/10 hover:border-white/20
              `}
            />
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label 
            htmlFor="password" 
            className="block text-sm font-medium text-slate-300 mb-2"
          >
            Contraseña
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="w-5 h-5 text-slate-500" aria-hidden="true" />
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu contraseña"
              autoComplete="current-password"
              disabled={isLoading}
              className={`
                w-full pl-12 pr-4 py-3 bg-white/5 border rounded-xl
                text-white placeholder-slate-500
                focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                focus:ring-offset-2 focus:ring-offset-slate-900
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                border-white/10 hover:border-white/20
              `}
            />
          </div>
        </div>

        {/* Error Message - Requirements 7.4 */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
          >
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: isLoading ? 1 : 1.02 }}
          whileTap={{ scale: isLoading ? 1 : 0.98 }}
          className={`
            w-full py-4 rounded-xl font-semibold text-lg
            flex items-center justify-center gap-3
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-blue-500/50 
            focus:ring-offset-2 focus:ring-offset-slate-900
            ${isLoading
              ? 'bg-slate-600 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/30'
            }
            text-white
          `}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
              Iniciando sesión...
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5" aria-hidden="true" />
              Iniciar Sesión
            </>
          )}
        </motion.button>
      </form>

      {/* Help Text */}
      <p className="mt-6 text-center text-sm text-slate-500">
        Usa las mismas credenciales que utilizas para acceder a Moodle
      </p>
    </motion.div>
  );
}

/**
 * Gets stored teacher token from localStorage
 * @returns {string|null} - JWT token or null if not found
 */
export function getTeacherToken() {
  return localStorage.getItem(TEACHER_TOKEN_KEY);
}

/**
 * Gets stored teacher data from localStorage
 * @returns {Object|null} - Teacher data or null if not found
 */
export function getTeacherData() {
  const data = localStorage.getItem(TEACHER_DATA_KEY);
  if (!data) return null;
  
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/**
 * Checks if teacher is authenticated and token is not expired
 * @returns {boolean} - True if authenticated
 */
export function isTeacherAuthenticated() {
  const token = getTeacherToken();
  const data = getTeacherData();
  
  if (!token || !data) return false;
  
  // Check if token is expired
  if (data.expiresAt) {
    const expiresAt = new Date(data.expiresAt);
    if (expiresAt <= new Date()) {
      // Token expired, clear storage
      clearTeacherAuth();
      return false;
    }
  }
  
  return true;
}

/**
 * Clears teacher authentication data from localStorage
 */
export function clearTeacherAuth() {
  localStorage.removeItem(TEACHER_TOKEN_KEY);
  localStorage.removeItem(TEACHER_DATA_KEY);
}
